import { formatDate } from "@/lib/utils";
import { useAuth } from "@/features/auth/auth-context";
import { UserAvatar } from "./UserAvatar";

/**
 * The signed-in staff member's own ID card, shown on their dashboard. All data
 * comes from /auth/me (the auth context) — no extra request — except the photo,
 * which UserAvatar fetches with auth.
 */
export function IDCard() {
  const { user } = useAuth();
  if (!user || user.customer_id) return null; // staff only

  const siteText =
    user.sites && user.sites.length > 0 ? user.sites.join(", ") : "All sites";

  const rows: [string, string | null | undefined][] = [
    ["Site", siteText],
    ["Employee ID", user.employee_code],
    ["Department", user.department_name],
    ["Joining date", user.joining_date ? formatDate(user.joining_date) : null],
    ["Phone", user.phone],
    ["Blood group", user.blood_group],
    ["Emergency contact", user.emergency_contact],
    ["Date of birth", user.date_of_birth ? formatDate(user.date_of_birth) : null],
  ];

  return (
    <section
      aria-label="Your ID card"
      className="overflow-hidden rounded-xl border border-border bg-card"
    >
      <div className="flex items-center gap-4 border-b border-border bg-primary/10 p-4">
        <UserAvatar
          userId={user.id}
          hasPhoto={Boolean(user.has_photo)}
          name={user.full_name}
          size={72}
        />
        <div className="min-w-0">
          <h2 className="truncate text-lg font-semibold">{user.full_name}</h2>
          <p className="truncate text-sm text-muted-foreground">
            {user.designation || "—"}
          </p>
          <p className="truncate text-xs text-muted-foreground">{user.email}</p>
        </div>
        <span className="ml-auto shrink-0 self-start rounded-full bg-primary/15 px-2.5 py-1 text-xs font-medium text-primary">
          {user.role_name || user.role_code.replace(/_/g, " ")}
        </span>
      </div>

      <dl className="grid grid-cols-2 gap-x-4 gap-y-3 p-4 sm:grid-cols-3">
        {rows.map(([label, value]) => (
          <div key={label} className="min-w-0">
            <dt className="text-xs text-muted-foreground">{label}</dt>
            <dd className="truncate text-sm">{value || "—"}</dd>
          </div>
        ))}
      </dl>
      {user.address && (
        <div className="border-t border-border px-4 py-3">
          <dt className="text-xs text-muted-foreground">Address</dt>
          <dd className="text-sm">{user.address}</dd>
        </div>
      )}
    </section>
  );
}
